import { useEffect, useState } from 'react';

import { DeviceIdProvider, useDeviceId } from '@1debit/device-intelligence';
import { GtmIframe } from 'iframes/gtm/GtmIframe';
import { OldGtmFireEffect } from 'iframes/gtm/OldGtmFireEffect';
import DowntimePage from 'pages/downtime';
import { useDispatch } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import styled, { css, ThemeProvider } from 'styled-components';

import { SET_LCM_PREFILL_DATA } from 'actions/setLCMPrefillData';

import PageLoader from 'components/Loaders/PageLoader';

import {
  AJS_ANONYMOUS_ID,
  CHIME_DEVICE_ID,
  REDIRECTED_FROM_WEB_APP,
  USER_REQUESTED_ENROLLMENT_START,
} from 'consts/storageConsts';

import RenderFormRoutes from 'containers/RenderFormRoutes';
import RenderPostEnrollmentRoutes from 'containers/RenderPostEnrollmentRoutes';

import { useNewAppExperiment } from 'explat/useNewAppExperiment';
import { useVendorsIframe } from 'explat/useVendorsIframe';

import useDownTimePage from 'features/useDownTimePage';

import { createArkoseScript } from 'hooks/useArkose';
import { storeEnrollmentStartedTime } from 'hooks/useCaptureTimeOnPage';
import useNuDetect, { createNuDetectScript } from 'hooks/useNuDetect';
import { useNuDataDisabled } from 'hooks/useNuDataDisabled';
import usePageLoaders from 'hooks/usePageLoaders';
import useRoutes from 'hooks/useRoutes';

import { requestEnrollmentStartCountEndpoint } from 'requests';

import { theme } from 'theme';

import clearSessionForNative from 'thunks/clearSessionForNative';
import resetEnrollment from 'thunks/resetEnrollment';

import decryptPrefillData from 'utils/decryptPrefillData';
import { deviceIntelligenceEnvironment } from 'utils/deviceIntelligenceUtils';
import { initializeEventRouter } from 'utils/eventRouter';
import { buildChimeWebLink } from 'utils/linkHelper';
import { LOG_LEVELS, loggerHelper } from 'utils/logger/loggerHelper';
import {
  IS_EMBEDDED,
  IS_PRELOAD,
  IS_WEB_APP_SRC,
} from 'utils/queryParamHelpers';
import { anonymousIdCookieMatchQSP } from 'utils/segment';
import cookies from 'utils/storage/cookies';
import { shouldTrackPfPublicIdCookie } from 'utils/storage/pfPublicId';
import {
  getFromLocalStorage,
  getValueFromStorage,
  setInLocalStorage,
  setValueInStorage,
} from 'utils/storage/storageHelper';

const AppWrapper = styled.div`
  font-family: ${props => props.theme.fonts.primaryFont};
  color: ${props => props.theme.colors['content-primary']};
  background-color: ${props => props.theme.colors.white};
  min-height: 100%;

  ${props => css`
    ${props.theme.media.desktop`
        background-color: ${props => props.theme.colors.alabaster};
      `};
  `};
`;

const InvisibleDiv = styled.div`
  height: 1px;
`;

function useConstructor(callback) {
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  if (hasBeenCalled || !callback) {
    return;
  }
  callback();
  setHasBeenCalled(true);
}

/**
 * Returns boolean for if new EMBEDDED (in-app webview) enrollment session
 *
 * The only way we can identify that user killed app and tries to return to fresh session is if
 * anonymousId cookie does not match one from QSP while EMBEDDED QSP === true or if QSP's PRELOAD === true
 *
 * Cases:
 * - When enrollment preloads, depending on what mobile build (5.19 or 5.20),
 *  the anonymousId QSP is either null or has a value that does not match previously stored one
 * - When anonymousID QSP is available, we must take on that new value
 *
 * @returns {boolean}
 */
function enrollmentSessionWasRestartedInNative() {
  return (!anonymousIdCookieMatchQSP() && IS_EMBEDDED) || IS_PRELOAD;
}

const requestEnrollmentStart = () => {
  const requestEnrollmentStart = getValueFromStorage(
    USER_REQUESTED_ENROLLMENT_START
  );

  if (!requestEnrollmentStart) {
    requestEnrollmentStartCountEndpoint();
    setValueInStorage(USER_REQUESTED_ENROLLMENT_START, 'true');
  }
};

const App = () => {
  const dispatch = useDispatch();
  const { userProgressIndex } = useRoutes();

  const shouldShowDownTimePage = useDownTimePage();

  const { clearBehaviouralData } = useNuDetect();
  const { isNudataDisabled } = useNuDataDisabled();
  const alreadyRedirectedFromWebApp = getFromLocalStorage(
    REDIRECTED_FROM_WEB_APP
  );

  const { deviceId } = useDeviceId();
  setInLocalStorage(CHIME_DEVICE_ID, deviceId);

  useConstructor(async () => {
    shouldTrackPfPublicIdCookie();
    createArkoseScript();
    if (!isNudataDisabled()) {
      createNuDetectScript();
    }
    initializeEventRouter();

    if (enrollmentSessionWasRestartedInNative()) {
      clearBehaviouralData();
      dispatch(clearSessionForNative());
    }

    const searchParams = new URL(document?.location).searchParams;
    if (searchParams.get('ne_pf_url')) {
      const encryptedParam = searchParams.get('ne_pf_url');

      try {
        const decryptedData = await decryptPrefillData(
          encryptedParam,
          process.env.PUBLIC_LCM_PREFILL_KEY
        );

        const parsedDecryptedData = JSON.parse(decryptedData);

        dispatch({
          type: SET_LCM_PREFILL_DATA,
          payload: parsedDecryptedData,
        });
      } catch (err) {
        loggerHelper({
          level: LOG_LEVELS.ERROR,
          message: 'Error parsing lcm prefill data',
          info: err.message,
        });
      }
    }

    if (IS_WEB_APP_SRC && !alreadyRedirectedFromWebApp) {
      // restart their enrollment if newly redirected from web app
      setInLocalStorage(REDIRECTED_FROM_WEB_APP, true);
      dispatch(resetEnrollment());
    }
    const isFirstPage = userProgressIndex === 1;

    if (isFirstPage) {
      storeEnrollmentStartedTime();
    }

    if (!IS_PRELOAD) {
      if (document.prerendering) {
        document.addEventListener(
          'prerenderingchange',
          requestEnrollmentStart,
          { once: true }
        );
      } else {
        requestEnrollmentStart();
      }
    }
  });

  const {
    isVariant: isVendorsIframeVariant,
    isLoaded: isVendorsExperimentLoaded,
  } = useVendorsIframe();

  const {
    isTreatment: isNewAppExperiment,
    isLoaded: isNewAppExperimentLoaded,
  } = useNewAppExperiment();

  const { showPageLoaders, hidePageLoaders } = usePageLoaders();

  // Handle experiment loading state with Redux page loader
  useEffect(() => {
    if (!isNewAppExperimentLoaded) {
      showPageLoaders();
    } else {
      hidePageLoaders();
    }
  }, [isNewAppExperimentLoaded, showPageLoaders, hidePageLoaders]);

  if (isNewAppExperimentLoaded && isNewAppExperiment) {
    const currentSearchParams = window.location.search;
    const redirectUrl =
      buildChimeWebLink('/enroll/account') + currentSearchParams;
    window.location.href = redirectUrl;
  }

  return (
    <ThemeProvider theme={theme}>
      <AppWrapper>
        <PageLoader />
        {isVendorsExperimentLoaded ? (
          isVendorsIframeVariant ? (
            <GtmIframe />
          ) : (
            <OldGtmFireEffect />
          )
        ) : null}
        {!IS_EMBEDDED && (
          <InvisibleDiv inert="true">
            <img
              alt=""
              role="presentation"
              width="1"
              height="1"
              src={`https://idsync.rlcdn.com/713261.gif?partner_uid=${cookies.get(AJS_ANONYMOUS_ID)}`}
            />
          </InvisibleDiv>
        )}

        {shouldShowDownTimePage ? (
          <DowntimePage />
        ) : (
          <HashRouter>
            <DeviceIdProvider environment={deviceIntelligenceEnvironment}>
              <RenderFormRoutes />
              <RenderPostEnrollmentRoutes />
            </DeviceIdProvider>
          </HashRouter>
        )}
      </AppWrapper>
    </ThemeProvider>
  );
};

App.propTypes = {};

export default App;
